import { useQueryClient } from '@tanstack/react-query';
import { Capacitor } from '@capacitor/core';
import { Badge, type BadgeTone } from '@hrms/ui';
import type { CompanyPolicyStatus, GroupPolicyAccess } from '@hrms/shared';
import { openBase64File } from '../../lib/base64-file.js';

/**
 * Pieces shared by the employee and admin company-policy pages (ADR 0026). Not the leave policies
 * page (`routes/Policies.tsx`), which is a different module.
 *
 * Kept free of the Markdown renderer on purpose — the policy body is `PolicyBodyText.tsx`, imported
 * directly by the pages that show one. Announcements (and through them the Home page) reuse helpers
 * from here, so anything heavy added to this file lands in the app's first load.
 */

/** Every company-policy query sits under this key, so one invalidation refreshes both views. */
export const COMPANY_POLICY_QUERY_KEY = ['company-policies'] as const;

export function useInvalidateCompanyPolicies() {
  const queryClient = useQueryClient();
  return () => void queryClient.invalidateQueries({ queryKey: COMPANY_POLICY_QUERY_KEY });
}

export function policyErrorMessage(error: unknown): string {
  if (error instanceof Error) return "Can't reach the server. Check your connection and try again.";
  const body = (error as { body?: unknown } | undefined)?.body;
  if (body && typeof body === 'object' && 'message' in body && typeof body.message === 'string') {
    return body.message;
  }
  return 'Something went wrong. Please try again.';
}

const STATUS_TONE: Record<CompanyPolicyStatus, BadgeTone> = {
  draft: 'neutral',
  scheduled: 'info',
  active: 'success',
  archived: 'neutral',
};

/** Sentence case like every other module's status pill — never the raw enum. */
const STATUS_LABEL: Record<CompanyPolicyStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  active: 'Active',
  archived: 'Archived',
};

/** The same label and tone the status badge uses, for the per-company chips of the group list. */
export const POLICY_STATUS_LABEL = STATUS_LABEL;
export const POLICY_STATUS_TONE = STATUS_TONE;

/**
 * Why a company of the group cannot be used for policies right now (docs/adr/0037-group-policies.md),
 * or null when it can. Shown beside the greyed company in the scope control.
 */
export function groupAccessReason(access: GroupPolicyAccess): string | null {
  switch (access) {
    case 'no_access':
      return "You can't manage policies here";
    case 'module_off':
      return 'Policies are switched off here, or the trial has ended';
    case 'unavailable':
      return "Couldn't be reached just now";
    default:
      return null;
  }
}

export function PolicyStatusBadge({ status }: { status: CompanyPolicyStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>;
}

// Moved to lib (no Markdown there) so pages that only upload or open a file don't load the renderer.
export { fileToBase64 } from '../../lib/base64-file.js';

/**
 * A file name safe to hand to the filesystem: no path separators or characters Android/Windows
 * reject. A policy title ("Leave / Travel") is typed text, and `Filesystem.writeFile` reads a
 * "/" in `path` as a sub-directory that does not exist.
 */
export function safeFileName(name: string, fallback: string): string {
  const cleaned = name
    .replace(/[\\/:*?"<>|]+/g, '-')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 120);
  return cleaned && cleaned !== '-' ? cleaned : fallback;
}

/** The person closed the share sheet without choosing anything: not an error worth showing. */
function isShareCancelled(error: unknown): boolean {
  return error instanceof Error && /cancel/i.test(error.message);
}

/** What to show when saving or opening a file failed (not the network: the file was already here). */
export function fileSaveErrorMessage(): string {
  return "Couldn't save or open the file. Please try again.";
}

/**
 * Shows a downloaded policy file (the API's base64 file shape). In a browser it opens in a new tab,
 * as before. Inside the Android app (Capacitor) `window.open` on a blob URL goes nowhere, so the
 * file is written to the app cache and handed to the share sheet instead (`saveOrShareBlob`),
 * which lets the person open it in a PDF viewer or save it. The plugins load on demand: the Home
 * page reaches this file through announcements and does not need them.
 */
export async function openPolicyFile(file: {
  fileName?: string;
  mimeType: string;
  contentBase64: string;
}): Promise<void> {
  if (!Capacitor.isNativePlatform()) {
    openBase64File(file);
    return;
  }
  const binary = atob(file.contentBase64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  const { saveOrShareBlob } = await import('../../lib/saveBlob.js');
  try {
    await saveOrShareBlob(
      new Blob([bytes], { type: file.mimeType }),
      safeFileName(file.fileName ?? '', 'policy.pdf'),
    );
  } catch (error) {
    if (!isShareCancelled(error)) throw error;
  }
}

/**
 * Saves rows as a CSV file: a normal download in a browser, the share sheet inside the Android app
 * (a plain `<a download>` click does nothing there). Same escaping rules as `downloadCsv`.
 */
export async function saveCsv(
  filename: string,
  rows: readonly (readonly (string | number | null | undefined)[])[],
): Promise<void> {
  const [{ toCsvText }, { saveOrShareBlob }] = await Promise.all([
    import('../../lib/csv-download.js'),
    import('../../lib/saveBlob.js'),
  ]);
  try {
    await saveOrShareBlob(
      new Blob([toCsvText(rows)], { type: 'text/csv;charset=utf-8' }),
      safeFileName(filename, 'export.csv'),
    );
  } catch (error) {
    if (!isShareCancelled(error)) throw error;
  }
}
